import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { doodleBg } from "../../appAssets";
import { DrawingType, getRandomSheet, pencilBox } from "./config";
import { useDoodling } from "../../hooks/useDoodling";
import DisplayPopUp from "./components/DisplayPopUp/DisplayPopUp";
import DrawingCanvas from "./components/DrawingCanvas/DrawingCanvas";
import SuccessPopup from "./components/SuccessPopup/SuccessPopup";
import { trackEvent } from "../../../mixpanel";
import { TrackingEvents } from "../../Utilities/analytics";
import "./Doodling.scss";

/**
 * Mirrors srcMobile/Screens/FunBoxScreen/Doodling/index.tsx
 *
 * Flow:
 *  1. DisplayPopUp asks "How would you like to doodle?" — pick doodle or color
 *  2. If color → fetch /getDoodles → pick a random colour sheet to overlay
 *  3. DrawingCanvas renders the toolbar, sticker tray, SVG drawing surface
 *  4. On Save → convert SVG → PNG dataURL → POST /v3/captureDoodle (multipart)
 *  5. On success → success popup → back to rooms
 */

// Convert SVG-PNG dataURL → Blob for multipart upload
function dataUrlToBlob(dataUrl: string): Blob | null {
  if (!dataUrl) return null;
  const [meta, base64] = dataUrl.split(",");
  if (!base64) return null;
  const mime = meta.match(/:(.*?);/)?.[1] || "image/png";
  try {
    const bin = atob(base64);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    return new Blob([arr], { type: mime });
  } catch {
    return null;
  }
}

function Doodling() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isOnboardingDone } = (location.state ?? {}) as {
    isOnboardingDone?: boolean;
  };
  console.log(isOnboardingDone, "isOnboardingDoneisOnboardingDone");
  const { postDoodleData, fetchColourSheets } = useDoodling();

  const [drawingType, setDrawingType] = useState<DrawingType>("doodle");
  const [colorSheetUrl, setColorSheetUrl] = useState<string>("");
  const [showPopup, setShowPopup] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch colour sheets when user picks "color" mode
  useEffect(() => {
    if (drawingType !== "color") return;
    let cancelled = false;
    (async () => {
      const sheets = await fetchColourSheets();
      if (cancelled) return;
      if (!sheets || sheets.length < 3) return;
      setColorSheetUrl(getRandomSheet(sheets)?.image || "");
    })();
    return () => {
      cancelled = true;
    };
  }, [drawingType, fetchColourSheets]);

  const handleBackToRooms = () => navigate(-1);

  const handleModeSelected = (mode: DrawingType) => {
    trackEvent(TrackingEvents.doodle_method_selected, { method: mode });
    setDrawingType(mode);
    setShowPopup(false);
  };

  // Save: upload doodle PNG to /v3/captureDoodle, show success on 200
  const handleCanvasSave = async (dataUrl: string, stickersUsed: number) => {
    const blob = dataUrlToBlob(dataUrl);
    if (!blob) return;

    setIsLoading(true);
    trackEvent(TrackingEvents.doodle_saved, {
      stickers_used: stickersUsed,
      method: drawingType,
    });
    const formData = new FormData();
    formData.append("file", blob, "doodle.png");
    formData.append("contentType", "Doodle");

    const res: any = await postDoodleData(formData);
    setIsLoading(false);
    if (res?.data) setShowSuccess(true);
  };

  return (
    <div className="doodling" style={{ backgroundImage: `url(${doodleBg})` }}>
      <DrawingCanvas
        onBack={() => {
          trackEvent(TrackingEvents.object_close_icon_clicked, {
            from: "doodling",
          });
          handleBackToRooms();
        }}
        onSave={handleCanvasSave}
        drawingType={drawingType}
        colorSheetUrl={colorSheetUrl}
        isDisplayPopup={showPopup || showSuccess}
        isOnboardingDone={isOnboardingDone}
      />

      {showPopup && (
        <DisplayPopUp
          onContinue={handleModeSelected}
          onClose={() => setShowPopup(false)}
        />
      )}

      {showSuccess && (
        <SuccessPopup
          titleText="That's amazing art work!"
          btnText="Woohoo"
          onPress={() => {
            setShowSuccess(false);
            handleBackToRooms();
          }}
          onClose={handleBackToRooms}
        />
      )}

      {isLoading && (
        <div className="doodling__loader-overlay">
          <div className="doodling__loader" />
        </div>
      )}
    </div>
  );
}

export default Doodling;
